import {type Schema, Validator} from "@cfworker/json-schema";
import {
  dashboardA2UISchema,
  type DashboardA2UIComponent as Component,
  type DashboardA2UISurface as DevplanDashboardA2UISurface,
} from "@devplaninc/codeup";

export const DASHBOARD_A2UI_SCHEMA_VERSION = "dashboard-a2ui-surface-v1";
export const DASHBOARD_A2UI_VERSION = "v0.9";
export const DASHBOARD_A2UI_CATALOG_ID = "devplan.com:dashboard";

const supportedComponentNames = new Set([
  "Column",
  "Row",
  "Text",
  "Metric",
  "Progress",
  "Chart",
  "Table",
  "Card",
  "CardsList",
  "Timeline",
  "Calendar",
  "Graph",
  "RankedList",
  "ActivityFeed",
  "Kanban",
  "Callout",
]);

const supportedDashboardProperties = [
  "schema_version",
  "a2ui_version",
  "catalog_id",
  "surface_id",
  "title",
  "summary",
  "icon",
] as const;

const legacyInstructions = "Legacy persisted component without refresh instructions; regenerate this dashboard to populate exact source/query instructions.";

type LooseA2UIComponent = Record<string, unknown> & {
  id: string;
  component: Record<string, unknown>;
  description?: unknown;
  instructions?: unknown;
};

const dashboardA2UIValidator = new Validator(dashboardA2UISchema as unknown as Schema, "2020-12", false);

export function parseDashboardA2UIJSON(source: string | undefined): DevplanDashboardA2UISurface | undefined {
  if (!source) return undefined;
  try {
    return parseDashboardA2UIValue(JSON.parse(source));
  } catch {
    return undefined;
  }
}

export function dashboardA2UITitle(source: string | undefined): string | undefined {
  return parseDashboardA2UIJSON(source)?.title;
}

export function parseDashboardA2UIValue(value: unknown): DevplanDashboardA2UISurface | undefined {
  const normalized = normalizeDashboardA2UIJSON(value);
  return dashboardA2UIValidator.validate(normalized).valid ? normalized as DevplanDashboardA2UISurface : undefined;
}

function normalizeDashboardA2UIJSON(value: unknown): unknown {
  const cleaned = removeNullFields(value);
  if (!isPlainObject(cleaned)) return cleaned;

  return {
    ...pickProperties(cleaned, supportedDashboardProperties),
    messages: normalizeDashboardA2UIMessages(cleaned.messages),
  };
}

function normalizeDashboardA2UIMessages(messages: unknown): unknown {
  if (!Array.isArray(messages)) return messages;

  const normalizedMessages: unknown[] = [];
  for (const message of messages) {
    if (!isPlainObject(message)) continue;
    if (isPlainObject(message.createSurface)) {
      normalizedMessages.push({
        version: message.version,
        createSurface: pickProperties(message.createSurface, ["surfaceId", "catalogId"] as const),
      });
      continue;
    }
    if (!isPlainObject(message.updateComponents) || !Array.isArray(message.updateComponents.components)) {
      continue;
    }

    const components = message.updateComponents.components
      .filter(isSupportedComponent)
      .map(component => {
        const {id, description, icon, evidence} = component;
        return {
          id,
          ...(typeof description === "string" && description.trim().length > 0 ? {description} : {}),
          ...(typeof icon === "string" && icon.trim().length > 0 ? {icon} : {}),
          ...(Array.isArray(evidence) ? {evidence} : {}),
          instructions: typeof component.instructions === "string" && component.instructions.trim().length >= 40
            ? component.instructions
            : legacyInstructions,
          component: normalizeSupportedComponentValue(component.component),
        };
      });
    const ids = new Set(components.map(component => component.id).filter(id => typeof id === "string"));

    normalizedMessages.push({
      version: message.version,
      updateComponents: {
        surfaceId: message.updateComponents.surfaceId,
        components: components.map(component => normalizeLayoutChildren(component, ids)),
      },
    });
  }
  return normalizedMessages;
}

function isSupportedComponent(value: unknown): value is LooseA2UIComponent {
  if (!isPlainObject(value) || typeof value.id !== "string" || !isPlainObject(value.component)) {
    return false;
  }
  return Object.keys(value.component).some(key => supportedComponentNames.has(key));
}

function normalizeSupportedComponentValue(value: Record<string, unknown>): Record<string, unknown> {
  const supported = Object.entries(value).find(([key]) => supportedComponentNames.has(key));
  return supported ? Object.fromEntries([supported]) : {};
}

function normalizeLayoutChildren(component: LooseA2UIComponent, ids: Set<string>) {
  const value = component.component;
  if (!isPlainObject(value)) return component;
  const normalized = {...value};
  if ("Column" in normalized) {
    normalized.Column = normalizeLayoutValue(normalized.Column, ids);
  }
  if ("Row" in normalized) {
    normalized.Row = normalizeLayoutValue(normalized.Row, ids);
  }
  return {
    ...component,
    component: normalized,
  };
}

function normalizeLayoutValue(value: unknown, ids: Set<string>) {
  if (!isPlainObject(value) || !Array.isArray(value.children)) return value;
  return {
    ...value,
    children: value.children.filter(childId => typeof childId === "string" && ids.has(childId)),
  };
}

function removeNullFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(removeNullFields);
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== null)
      .map(([key, entry]) => [key, removeNullFields(entry)]),
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pickProperties<const Key extends string>(value: Record<string, unknown>, keys: readonly Key[]): Partial<Record<Key, unknown>> {
  return Object.fromEntries(keys.flatMap(key => key in value ? [[key, value[key]]] : [])) as Partial<Record<Key, unknown>>;
}

export function collectDashboardA2UIComponents(document: DevplanDashboardA2UISurface): Map<string, Component> {
  const components = new Map<string, Component>();
  for (const message of document.messages) {
    if (!("updateComponents" in message)) continue;
    for (const component of message.updateComponents.components) {
      components.set(component.id, component);
    }
  }
  return components;
}
